import { useEffect, useMemo, useRef, useState } from 'react'
import { machineNameOf } from '../agents/machineName'
import { Sheet } from '../ui/Sheet'
import { PickerSheet } from '../ui/PickerSheet'
import { Icon } from '../ui/Icon'
import { PixelPet } from '../identity/PixelPet'
import { useNotes } from './useNotes'
import { ghostReceivers } from './ghosts'
import { AgentMention, findMention, pingTag } from '../agents/AgentMention'
import {
  notesDeliver, noteFileUpload } from '../transport/api'
import { NoteAttachments } from './NoteAttachments'
import type { AgentRow, Note, NoteRef, NoteReceiver } from '../transport/api'
import { getArchivedNotes } from '../transport/api'
import { useQuery } from '@tanstack/react-query'
import { useVoiceRecorder } from '../voice/useVoiceRecorder'
import { registerMicHotkey, MIC_HOTKEY_LABEL } from '../voice/micHotkey'
import { transcribeBlob } from '../voice/transcribe'
import { insertAtCaret } from '../voice/insertAtCaret'
import { enterSends, keepEnter } from '../ui/enterSends'
import { micGate, recordCapMs, reportVoiceError } from '../billing/voiceGate'

/**
 * "Take a note" — the panel behind the right-to-left swipe (and the desktop
 * button), per the owner's design.
 *
 * Two tabs, because the two things you do with notes happen at different times:
 * WRITE while an agent is busy and you must not interrupt it, and DISPATCH once
 * it is free. Keeping them apart means neither view has to carry the other's
 * controls, which is what keeps a panel this dense readable.
 *
 * The list is segmented rather than tabbed a second time: Queue / Done / Archive
 * are three states of the same objects, and a segmented control says that where
 * three top-level tabs would imply three different things.
 */

const sameRef = (a: NoteRef, b: NoteRef) =>
  a.session === b.session && (a.host ?? null) === (b.host ?? null)

const label = (r: NoteRef) => (r.host ? `${r.session} @ ${r.host}` : r.session)

/**
 * A receiver that DID the note: the edge detaches it when the note is delivered to
 * its pane, and separately marks it executed when it runs. Either means "this got
 * done", as opposed to a receiver still sitting attached in a queue.
 */
const didIt = (r: NoteReceiver) => r.detached_ts != null || r.state === 'executed'

/**
 * A note belongs in DONE once it was dispatched or run — NOT filed away by hand.
 *
 * Keyed on the note's OWN receivers, never on the open agent. The earlier version
 * asked `mine(note)?.state === 'executed'`, and mine() is undefined whenever the
 * panel is open on a different agent than the note was sent to (or on none) — so
 * every dispatched note fell through to Archive. It was also the wrong field: a
 * single-agent dispatch DETACHES the receiver (strikethrough in the UI) without
 * marking it executed, so `state === 'executed'` missed all of them. The edge
 * auto-archives a note once every receiver is detached (notes.ts maybeAutoarchive),
 * which is exactly how a note you ran ends up in the archive query. Owner,
 * 2026-08-14: Archive 應該是空的, Done 裡面應該是 Archive 現在的內容.
 */
const wasDispatched = (note: Note) => (note.receivers ?? []).some(didIt)

/** When a done note completed, for newest-first ordering in Done. */
const doneTs = (note: Note) =>
  Math.max(0, ...(note.receivers ?? []).map((r) => r.executed_ts ?? r.detached_ts ?? 0))

/** The agents a done note was sent to — re-attaching these is how Requeue un-archives it. */
const doneReceiverRefs = (note: Note): NoteRef[] =>
  (note.receivers ?? []).filter(didIt).map((r) => ({ host: r.host, session: r.session }))

type View = 'queue' | 'done' | 'archive'

/** One file being uploaded for the note in the composer. */
type PendingFile = {
  key: string
  name: string
  size: number
  pct: number
  /** Set once the server has the bytes; only these reach /notes/add. */
  fileId?: string
  error?: string
}

export function NotePanel({
  agentRef,
  agents,
  draft,
  onDraft,
  ghosts,
  onStatus,
  onClose,
}: {
  /** The agent whose queue this is; null when no agent is open. */
  agentRef: NoteRef | null
  /** Roster for the receiver picker. */
  agents: AgentRow[]
  /** Draft text — owned by App so dictation can write into it while this is open. */
  draft: string
  onDraft: (v: string) => void
  /** Sessions currently judged gone (see ghosts.ts). */
  ghosts: Set<string>
  onStatus: (msg: string) => void
  onClose: () => void
}) {
  const [view, setView] = useState<View>('queue')
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null)
  const [picking, setPicking] = useState<Note | null>(null)
  /** In-progress @mention in the draft, and the input it lives in. */
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null)
  const draftEl = useRef<HTMLTextAreaElement>(null)

  /*
   * VOICE INTO THE NOTE — owner, 2026-09-05: 把筆記本的功能也加入麥克風,就放在 Enter
   * 按鈕的左邊. The same three parts the composer uses (recorder → transcribe →
   * insert at the caret), pointed at this draft; `draft` is owned by App, so the
   * words go out through onDraft exactly as typing does. Every failure is one
   * English sentence on the status line, the service's own words when it has them.
   */
  const voice = useVoiceRecorder({
    onClip: async (blob, durationMs) => {
      try {
        const r = await transcribeBlob(blob, durationMs)
        if (!r.text.trim()) {
          onStatus('No speech was detected.')
          return
        }
        const el = draftEl.current
        const start = el?.selectionStart ?? draft.length
        const end = el?.selectionEnd ?? draft.length
        const next = insertAtCaret(draft, start, end, r.text)
        onDraft(next.value)
        requestAnimationFrame(() => {
          const box = draftEl.current
          if (!box) return
          box.focus()
          box.setSelectionRange(next.caret, next.caret)
        })
      } catch (e) {
        reportVoiceError(e, onStatus)
      }
    },
    onProblem: onStatus,
    maxMs: recordCapMs,
  })
  // The same gate the composer's mic uses (billing/voiceGate): an exhausted day
  // opens the plan sheet instead of the microphone.
  const micPress = () => {
    if (micGate(voice.state)) return
    voice.toggle()
  }
  // While this sheet is open, Ctrl+M means THIS microphone (micHotkey's stack: the
  // sheet registers on top of the composer's and hands the key back on close).
  const voiceToggleRef = useRef(micPress)
  voiceToggleRef.current = micPress
  useEffect(() => registerMicHotkey(() => voiceToggleRef.current()), [])
  const micRec = voice.state === 'recording'
  const micBusy = voice.state === 'starting' || voice.state === 'uploading'
  const micSecs = Math.floor(voice.elapsedMs / 1000)
  const micClock = `${Math.floor(micSecs / 60)}:${String(micSecs % 60).padStart(2, '0')}`

  /**
   * Grow the draft with its text, then let it scroll.
   *
   * A textarea does not size itself: at rows=2 it stayed two lines tall however
   * much you wrote, hiding everything above the last line — which matters more
   * here than in the composer, because a note is often dictated in one long
   * run and you cannot proofread what you cannot see.
   *
   * Height is cleared to `auto` first: scrollHeight only shrinks back when the
   * element is not already holding itself open at the taller size. The ceiling
   * is READ from the computed max-height rather than repeated here — writing
   * the number twice is how the stylesheet and the JS quietly stop agreeing.
   */
  useEffect(() => {
    const el = draftEl.current
    if (!el) return
    el.style.height = 'auto'
    const cs = getComputedStyle(el)
    /*
     * scrollHeight is content+padding, but `height` under border-box includes
     * the border — so setting one from the other leaves the box two pixels
     * short and it reports itself scrollable while showing everything. The
     * visible cost is a scrollbar thumb on a box with nothing hidden.
     */
    const border =
      Number.parseFloat(cs.borderTopWidth || '0') + Number.parseFloat(cs.borderBottomWidth || '0')
    const max = Number.parseFloat(cs.maxHeight)
    const want = el.scrollHeight + (Number.isFinite(border) ? border : 0)
    el.style.height = `${Number.isFinite(max) ? Math.min(want, max) : want}px`
  }, [draft])

  // The status line is this panel's ONLY error surface, so the hook gets it:
  // every mutation that the store refuses is said here rather than swallowed
  // (useNotes.ts — the onError block above `edit`).
  const n = useNotes(agentRef, onStatus)

  /**
   * ONE deliver-and-run lane, both buttons — owner, 2026-08-09: 我剛剛是按下
   * Agent 的按鈕才直接送出的, 把 "dispatch" 按鈕的行為也修改的一樣.
   *
   * The receiver ▶ chip and the Dispatch button now share this exact call:
   * /notes/deliver types the note (with its <note-file:> tags, composed
   * server-side) into the pane and submits; success detaches the receiver —
   * the note lives in the pane now.
   *
   * A BUSY AGENT QUEUES IT — owner, 2026-09-14: 基本上不該阻擋, 而是要將其 Queue 在
   * Message queue 裡面吧. This said "A busy pane REFUSES with its reason rather
   * than queueing: both buttons mean NOW". The ruling replaces that, and the
   * objection behind it is answered rather than dropped: the status line says
   * QUEUED, so the button still reports what actually happened.
   */
  const deliverNow = (note: Note, ref: NoteRef, session: string) => {
    void notesDeliver(note.id, ref)
      .then((res) => {
        if (res.delivered) {
          onStatus(`sent to ${session}'s terminal`)
          n.refresh()
        } else if (res.queued) {
          // Said plainly, and the note IS gone from the list (the edge detaches on
          // queue too, or the agent would run it twice), so the refresh belongs here
          // exactly as much as it does above.
          onStatus(`${session} is busy — queued, it will run when free`)
          n.refresh()
        } else {
          onStatus(res.detail || res.error || 'could not deliver')
        }
      })
      .catch((e: unknown) => {
        const err = e as {
          status?: number
          body?: { detail?: string; error?: string; delivered?: boolean }
        }
        /*
         * THE ONE FAILURE THAT ALREADY HAPPENED.
         *
         * /notes/deliver types the note into the pane and only THEN records the
         * detach; when the store refuses that record the edge answers 500
         * write_failed WITH `delivered: true`
         * (avengents-server-ts/src/wtserver/notes.ts:250-258) precisely so the
         * caller does not retry it into a second execution. Reading only
         * body.detail/error showed the bare code beside a row still marked
         * pending, and the obvious next move — press Dispatch again — RAN THE
         * NOTE TWICE. So say all three things, and refetch: the note really is
         * still queued (the detach did not persist) and the row must show that.
         */
        if (err.body?.delivered) {
          onStatus(`sent to ${session}'s terminal — but not saved, so do NOT send it again`)
          n.refresh()
          return
        }
        onStatus(
          err.status === 404
            ? `this note is not pending for ${session} — requeue it first`
            : (err.body?.detail ?? err.body?.error ?? `could not deliver to ${session}`),
        )
      })
  }
  const archived = useQuery({
    queryKey: ['notes-archive'],
    queryFn: getArchivedNotes,
    // Always, not only on the Archive tab — owner, 2026-08-14: Note 把 Done 的
    // Note 放到 Archive 欄位去了. A note dispatched to a single agent is executed
    // AND detached, and the edge auto-archives a note once every receiver is
    // detached (notes.ts maybeAutoarchive), so it leaves the main list and lands
    // in the archive query. To a person it is DONE — they ran it — not filed
    // away, so Done reclaims it below (and its tab count) and Archive lets it go.
    // The panel only mounts when the sheet is open, so this is one cached query.
  })

  /**
   * The three lists, with dispatched-then-auto-archived notes reclaimed for Done.
   *
   * `n.executed` is only the notes still in the main list (not archived), which
   * for a single-receiver dispatch is none — the edge auto-archives it the instant
   * every receiver detaches. So Done also pulls the archived notes that were
   * dispatched or run (wasDispatched, read off the receivers), and Archive keeps
   * only what was filed away by hand — archived while its receivers were still
   * attached. That is why Archive is now empty for the owner: nothing was hand-filed.
   */
  const archivedNotes = archived.data?.notes ?? []
  const doneRows = useMemo(() => {
    const reclaimed = archivedNotes.filter(wasDispatched)
    return [...n.executed, ...reclaimed].sort((a, b) => doneTs(b) - doneTs(a))
  }, [n.executed, archivedNotes])
  const archiveRows = useMemo(
    () => archivedNotes.filter((note) => !wasDispatched(note)),
    [archivedNotes],
  )

  /**
   * Files chosen for the note being written, uploaded as they are picked.
   *
   * Upload-then-create: a failure here leaves a file nothing references, which the
   * server sweeps, rather than a note the owner can see with its attachment missing.
   */
  const fileEl = useRef<HTMLInputElement>(null)
  const [pending, setPending] = useState<PendingFile[]>([])
  /** True while a file is over the note editor — see the drop handlers below. */
  const [dropping, setDropping] = useState(false)
  const uploading = pending.some((p) => !p.fileId && !p.error)

  const pickFiles = (files: FileList | null) => {
    for (const file of Array.from(files ?? [])) {
      const key = `${file.name}:${file.size}:${pending.length}:${file.lastModified}`
      setPending((v) => [...v, { key, name: file.name, size: file.size, pct: 0 }])
      const at = (fn: (p: PendingFile) => PendingFile) =>
        setPending((v) => v.map((p) => (p.key === key ? fn(p) : p)))
      void noteFileUpload(file, (pct) => at((p) => ({ ...p, pct })))
        .then((r) => at((p) => ({ ...p, pct: 100, fileId: r.file_id ?? '' })))
        .catch((e: unknown) =>
          at((p) => ({ ...p, error: e instanceof Error ? e.message : 'upload failed' })),
        )
    }
  }

  const save = () => {
    const text = draft.trim()
    // A note with only a file and no words is still a note worth keeping.
    if (!text && !pending.some((p) => p.fileId)) return
    // Saving now would drop whatever is still in flight without saying so. The chips
    // show their own progress, so the reason is on screen rather than in a message.
    if (uploading) return
    const fileIds = pending.map((p) => p.fileId).filter((id): id is string => !!id)
    n.add.mutate(
      { text, ...(fileIds.length ? { fileIds } : {}) },
      {
        onSuccess: () => {
          onDraft('')
          setPending([])
          // A note always lands in the queue, so show it landing.
          setView('queue')
        },
      },
    )
  }

  const pickerItems = useMemo(
    () =>
      agents.map((a) => ({
        id: `${a.host ?? ''}\0${a.session}`,
        label: a.session,
        sub: machineNameOf(a),
        pet: a.session,
      })),
    [agents],
  )

  const rows: Note[] =
    view === 'queue' ? n.pending : view === 'done' ? doneRows : archiveRows

  return (
    <>
      <Sheet
        title="Take a note"
        onClose={onClose}
        className="sheet-tall"
        /*
         * The composer is the sheet's ACTION row, so it is pinned to the bottom
         * while the list scrolls above it — the shape of every chat window, and
         * for the same reason: what you are writing and what you have already
         * written belong on screen together. It replaces a Write/Notes tab pair
         * that made you leave one to see the other, which is exactly the
         * switching the owner asked to remove.
         */
        actions={
          !n.unavailable ? (
            <div
              className={`nt-compose${dropping ? ' dropping' : ''}`}
              /*
               * DROPPING A FILE ON AN OPEN NOTE ATTACHES IT TO THE NOTE.
               *
               *   owner, 2026-08-07: Note 也要能上傳檔案！
               *
               * The app listens for drops at the WINDOW so every view accepts files, and
               * that handler stages them into the composer. With the note editor open
               * that is the wrong destination and a silent one: the file uploads, the
               * chip appears down in the conversation, and the note you were writing
               * gets nothing. The pick-a-file button here has always worked — only the
               * drop went somewhere else.
               *
               * `stopPropagation` is the whole fix. Without it BOTH this and the window
               * handler run, and the same file is attached twice to two different
               * places.
               */
              onDragOver={(e) => {
                if (!e.dataTransfer.types.includes('Files')) return
                e.preventDefault()
                e.stopPropagation()
                setDropping(true)
              }}
              onDragLeave={(e) => {
                // Fires for children too; only clear when the pointer really left.
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropping(false)
              }}
              onDrop={(e) => {
                if (!e.dataTransfer.types.includes('Files')) return
                e.preventDefault()
                e.stopPropagation()
                setDropping(false)
                pickFiles(e.dataTransfer.files)
              }}
            >
              {/* Same picker as the terminal composer, anchored above the input. */}
              {mention && (
                <AgentMention
                  agents={agents}
                  query={mention.query}
                  onPick={(a) => {
                    const before = draft.slice(0, mention.start)
                    const after = draft.slice(mention.start + 1 + mention.query.length)
                    const next = `${before}${pingTag(a)}${after}`
                    onDraft(next)
                    setMention(null)
                    requestAnimationFrame(() => {
                      const el = draftEl.current
                      if (!el) return
                      el.focus()
                      const at = before.length + pingTag(a).length
                      el.setSelectionRange(at, at)
                    })
                  }}
                  onDismiss={() => setMention(null)}
                />
              )}
              {pending.length > 0 && (
                <div className="nt-pending">
                  {pending.map((p) => (
                    <span
                      key={p.key}
                      className={`nt-chip${p.error ? ' err' : p.fileId ? ' done' : ''}`}
                      title={p.error ?? p.name}
                    >
                      <span className="nt-chip-name">{p.name}</span>
                      <span className="nt-chip-state">
                        {p.error ? 'failed' : p.fileId ? '' : `${p.pct}%`}
                      </span>
                      <button
                        type="button"
                        className="nt-chip-x"
                        aria-label={`Remove ${p.name}`}
                        onClick={() => setPending((v) => v.filter((x) => x.key !== p.key))}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <textarea
                ref={draftEl}
                className="nt-draft"
                value={draft}
                onChange={(e) => {
                  onDraft(e.target.value)
                  setMention(findMention(e.target.value, e.target.selectionStart ?? e.target.value.length))
                }}
                onKeyUp={(e) => {
                  const el = e.currentTarget
                  setMention(findMention(el.value, el.selectionStart ?? el.value.length))
                }}
                placeholder={agentRef ? `Note for ${agentRef.session}…` : 'Note (no agent open)…'}
                rows={2}
                spellCheck={false}
                onKeyDown={(e) => {
                  /*
                   * ENTER SAVES. The point of this whole row: dictating a note
                   * left you with text and no way to commit it without reaching
                   * for a button, which defeats hands-free capture. Shift+Enter
                   * still makes a newline.
                   *
                   * The guard is `defaultPrevented`, NOT the mention state.
                   * Checking state looked right and was measurably wrong: the
                   * picker listens on document at CAPTURE, and its setMention(null)
                   * comes from a non-React listener, which React flushes
                   * synchronously — so by the time the event bubbles here the
                   * component has already re-rendered with mention === null, and
                   * a state check reads "no picker open". Observed as: Enter
                   * inserted the ping tag AND saved it as a note in one press.
                   * defaultPrevented asks the only question that matters — has
                   * anything already handled this key — and cannot go stale.
                   */
                  if (enterSends(e)) {
                    keepEnter(e)
                    save()
                  }
                }}
              />
              <div className="nt-compose-btns">
                {/* Pops the soft keyboard, or puts it away again. On a phone the
                    input is often reached by dictation, so the keyboard is not
                    already up when you want to correct a word. */}
                {/* Any file, not only images: the owner asked for "image or any
                    kind of file", and a note is as often a log or a diff as a
                    screenshot. */}
                <input
                  ref={fileEl}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    pickFiles(e.target.files)
                    // Reset, or picking the same file twice in a row does nothing.
                    e.target.value = ''
                  }}
                />
                <button
                  type="button"
                  className="nt-cbtn"
                  aria-label="Keyboard"
                  title="Show or hide the keyboard"
                  onClick={() => {
                    const el = draftEl.current
                    if (!el) return
                    if (document.activeElement === el) el.blur()
                    else el.focus()
                  }}
                >
                  <Icon name="keyboard" size={18} />
                </button>
                {/* Mentions an agent INSIDE the note text — the ping tag travels
                    with the prompt. Which agents RECEIVE the note is a separate
                    thing, set by the chips on each row. */}
                <button
                  type="button"
                  className="nt-cbtn"
                  aria-label="Mention an agent"
                  title="Mention an agent"
                  onClick={() => {
                    const next = `${draft}@`
                    onDraft(next)
                    requestAnimationFrame(() => {
                      const el = draftEl.current
                      if (!el) return
                      el.focus()
                      el.setSelectionRange(next.length, next.length)
                      setMention(findMention(next, next.length))
                    })
                  }}
                >
                  @
                </button>
                {/* Order of the left cluster — owner, 2026-09-10: 小鍵盤 → @ → 檔案. The
                    attach button sits to the RIGHT of @, last of the three. */}
                <button
                  type="button"
                  className="nt-cbtn"
                  aria-label="Attach a file"
                  title="Attach a file"
                  onClick={() => fileEl.current?.click()}
                >
                  <Icon name="attach_file" size={18} />
                </button>
                {/* The microphone, immediately LEFT of save — the same position as in
                    the composer row, so the muscle memory transfers (notes.css says
                    why the auto margin moves onto it). */}
                <button
                  type="button"
                  className={`nt-cbtn mic${micRec ? ' rec' : micBusy ? ' busy' : ''}`}
                  disabled={micBusy}
                  onClick={micPress}
                  aria-label={micRec ? 'Stop recording' : 'Record voice input'}
                  aria-pressed={micRec}
                  title={
                    micRec
                      ? `Stop recording and insert the words (${MIC_HOTKEY_LABEL})`
                      : voice.state === 'uploading'
                        ? 'Transcribing…'
                        : `Record, then insert the words at the caret (${MIC_HOTKEY_LABEL})`
                  }
                >
                  {micRec ? <span className="mic-clock">{micClock}</span> : <Icon name="mic" size={18} />}
                </button>
                <button
                  type="button"
                  className="nt-save"
                  disabled={!draft.trim() || n.add.isPending}
                  onClick={save}
                  aria-label="Save note"
                  title="Save note (Enter)"
                >
                  <Icon name={n.add.isPending ? 'sync' : 'send'} size={18} />
                </button>
              </div>
            </div>
          ) : undefined
        }
      >
        {/* The view switch stays put while the list scrolls beneath it. */}
        <div className="nt-head">
          {n.unavailable === 'unavailable' && (
            <div className="nt-empty">This server has no notes backend yet.</div>
          )}
          {n.unavailable === 'locked' && (
            <div className="nt-empty">Vault locked — unlock to use notes.</div>
          )}
          {!n.unavailable && (
            <div className="vm-seg" role="radiogroup" aria-label="Which notes">
              {(
                [
                  ['queue', `Queue${n.pending.length ? ` ${n.pending.length}` : ''}`],
                  ['done', `Done${doneRows.length ? ` ${doneRows.length}` : ''}`],
                  ['archive', 'Archive'],
                ] as [View, string][]
              ).map(([id, text]) => (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={view === id}
                  className={`vm-seg-btn${view === id ? ' on' : ''}`}
                  onClick={() => setView(id)}
                >
                  {text}
                </button>
              ))}
            </div>
          )}
        </div>

        {!n.unavailable && (
          <div className="nt-list">
            {n.loading && <div className="nt-empty">loading…</div>}
            {!n.loading && rows.length === 0 && (
              <div className="nt-empty">
                {view === 'queue'
                  ? 'Nothing queued for this agent.'
                  : view === 'done'
                    ? 'Nothing dispatched yet.'
                    : 'Archive is empty.'}
              </div>
            )}

            {rows.map((note) => {
              const gone = ghostReceivers(note, ghosts)
              const isEditing = editing?.id === note.id
              return (
                <div key={note.id} className={`nt-row${view === 'done' ? ' done' : ''}`}>
                  {isEditing ? (
                    <textarea
                      className="nt-draft"
                      value={editing.text}
                      rows={3}
                      onChange={(e) => setEditing({ id: note.id, text: e.target.value })}
                      /*
                       * The editor closes when the save LANDS, not when the
                       * blur fires.
                       *
                       * setEditing(null) used to run on the line after the
                       * mutate, outside it — so a refused write (the edge now
                       * answers 500 write_failed, notes.ts:115) closed the
                       * editor and threw away text the server had just said it
                       * did not have. The typing has to outlive the failure,
                       * because retyping it is the only other copy. Nothing
                       * to save still closes immediately: there is nothing to
                       * lose and nothing to wait for.
                       */
                      onBlur={() => {
                        if (!editing.text.trim() || editing.text === note.text) {
                          setEditing(null)
                          return
                        }
                        n.edit.mutate(
                          { id: note.id, text: editing.text.trim() },
                          { onSuccess: () => setEditing(null) },
                        )
                      }}
                      // autofocus is deliberate here (fossil eslint-disable removed 2026-09-02 — that plugin was never installed)
                      autoFocus
                    />
                  ) : (
                    <button
                      type="button"
                      className="nt-text"
                      onClick={() => setEditing({ id: note.id, text: note.text })}
                      title="Edit"
                    >
                      {note.text}
                    </button>
                  )}

                  {/* Outside the edit branch: editing changes the text, and the files
                      stay put either way. Shown for archived notes too — archiving is
                      reversible and does not remove the files, so hiding them would
                      say something the store does not. */}
                  <NoteAttachments items={note.attachments ?? []} />

                  {/*
                    * Where it came FROM, shown only when it was not the owner.
                    * A note an agent wrote is read differently from one you
                    * wrote yourself — you are checking its judgement, not
                    * recalling your own — so the queue has to say which it is.
                    * Absent on notes predating agent authorship, which is
                    * correct: those were all yours.
                    */}
                  {note.created_by?.kind === 'agent' && (
                    <div className="nt-from">
                      <PixelPet
                        name={note.created_by.label ?? note.created_by.hash ?? '?'}
                        seed={note.created_by.hash}
                        size={13}
                      />
                      from {note.created_by.label ?? note.created_by.hash?.slice(0, 8) ?? 'an agent'}
                    </div>
                  )}

                  {/* Who this note is for. The chips ARE the control: tap ＋ to
                      attach more agents, which is also how a note moves. */}
                  <div className="nt-recv">
                    {(note.receivers ?? [])
                      /*
                       * Detached receivers are hidden in the QUEUE (they are not
                       * going to do anything) but shown in Done and Archive,
                       * where "who has had this" is the whole point of looking.
                       * The server deliberately returns the raw array and lets
                       * the caller decide, so this is the decision.
                       */
                      .filter((r) => !r.detached_ts || view !== 'queue')
                      // Server order is undefined; without this the chips
                      // reshuffle on every poll.
                      .sort((a, b) =>
                        `${a.host ?? ''}${a.session}`.localeCompare(`${b.host ?? ''}${b.session}`),
                      )
                      .map((r) => {
                        const ref = { host: r.host, session: r.session }
                        const isGhost = gone.some((g) => sameRef(g, ref))
                        const isMe = agentRef && sameRef(ref, agentRef)
                        return (
                          <span
                            key={`${r.host ?? ''}\0${r.session}`}
                            className={
                              `nt-chip${isMe ? ' me' : ''}${isGhost ? ' ghost' : ''}` +
                              (r.detached_ts ? ' gone' : '')
                            }
                            title={
                              r.detached_ts
                                ? `${label(ref)} — no longer attached`
                                : isGhost
                                  ? `${label(ref)} is no longer running — tap to detach`
                                  : label(ref)
                            }
                          >
                            <PixelPet name={r.session} size={14} />
                            {r.session}
                            {r.state === 'executed' && <span className="nt-tick">✓</span>}
                            {isGhost ? (
                              <button
                                type="button"
                                className="nt-chip-x"
                                aria-label={`Detach ${r.session}`}
                                onClick={() => {
                                  n.receivers.mutate({ id: note.id, detach: [ref] })
                                  onStatus(`${r.session} detached — it is no longer running`)
                                }}
                              >
                                ×
                              </button>
                            ) : (
                              !r.detached_ts &&
                              r.state === 'pending' && (
                                /* The LIVE half of Detach — owner, 2026-08-08: 让 Notes
                                   里面的 Detach 可以直接进入 Terminal 送出并且执行.
                                   The note's text (with its <note-file:> tags) is typed
                                   into this agent's pane and submitted; on success the
                                   receiver detaches — the note lives in the pane now,
                                   and a queued copy would run a second time. A busy
                                   pane refuses with its reason instead of queueing:
                                   the button means NOW. */
                                <button
                                  type="button"
                                  className="nt-chip-x run"
                                  aria-label={`Send ${note.id} to ${r.session}'s terminal and run it`}
                                  title="Send to terminal & run"
                                  onClick={() => deliverNow(note, ref, r.session)}
                                >
                                  ▶
                                </button>
                              )
                            )}
                          </span>
                        )
                      })}
                    <button
                      type="button"
                      className="nt-chip add"
                      onClick={() => setPicking(note)}
                      title="Send this note to other agents too"
                    >
                      ＋
                    </button>
                  </div>

                  <div className="nt-acts">
                    {view !== 'archive' && agentRef && !note.archived && (
                      /* Same lane as the receiver ▶ — dispatch means DO IT, not
                         "park it in the composer" (that stop-over was the ported
                         v1 behaviour, retired at the owner's third ask).
                         A reclaimed (archived) note in Done has already run; it
                         offers Requeue, not another Dispatch. */
                      <button
                        type="button"
                        className="nt-act go"
                        title={`Send to ${agentRef.session}'s terminal & run`}
                        onClick={() => deliverNow(note, agentRef, agentRef.session)}
                      >
                        <Icon name="send" size={14} /> Dispatch
                      </button>
                    )}
                    {view === 'done' && (
                      <button
                        type="button"
                        className="nt-act"
                        title="Put it back in the queue"
                        // A reclaimed note (dispatched then auto-archived, shown in
                        // Done) needs un-archiving to requeue: re-attaching the
                        // agent(s) it was sent to does both — the note is archived
                        // precisely because they detached — and works with no agent
                        // open, unlike re-adding the current one. A plain
                        // (non-archived) executed note just flips its state.
                        onClick={() => {
                          if (note.archived) {
                            const back = doneReceiverRefs(note)
                            if (!back.length) return
                            n.receivers.mutate(
                              { id: note.id, add: back },
                              { onSuccess: () => void archived.refetch() },
                            )
                          } else {
                            n.setState.mutate({ id: note.id, state: 'pending' })
                          }
                        }}
                      >
                        <Icon name="undo" size={14} /> Requeue
                      </button>
                    )}
                    {view === 'archive' ? (
                      <button
                        type="button"
                        className="nt-act"
                        title="Bring it back into this agent's queue"
                        disabled={!agentRef}
                        onClick={() => {
                          if (!agentRef) return
                          // Attaching un-archives, by the server's definition:
                          // being in a queue and being archived are exclusive.
                          n.receivers.mutate(
                            { id: note.id, add: [agentRef] },
                            { onSuccess: () => void archived.refetch() },
                          )
                        }}
                      >
                        <Icon name="undo" size={14} /> Restore
                      </button>
                    ) : note.archived ? (
                      // A reclaimed note shown in Done is already archived — no
                      // Archive button, or it would offer to file away what is
                      // filed. Requeue above is its way back.
                      null
                    ) : (
                      <button
                        type="button"
                        className="nt-act"
                        title="Park it in the archive"
                        onClick={() => n.archive.mutate({ id: note.id, archived: true })}
                      >
                        <Icon name="content_paste" size={14} /> Archive
                      </button>
                    )}
                    <button
                      type="button"
                      className="nt-act danger"
                      title="Delete permanently"
                      onClick={() =>
                        n.remove.mutate(note.id, {
                          onSuccess: () => void archived.refetch(),
                        })
                      }
                    >
                      <Icon name="delete" size={14} />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Sheet>

      {picking && (
        <PickerSheet
          title="Also send to"
          placeholder="Search agents…"
          items={pickerItems}
          confirmLabel={(c) => `Attach ${c}`}
          busy={n.receivers.isPending}
          emptyText="No agents detected."
          onConfirm={(ids) => {
            const refs = ids.map((i) => {
              const [host, session] = i.split('\0')
              return { host: host || null, session: session! }
            })
            const already = new Set(
              (picking.receivers ?? []).filter((r) => !r.detached_ts).map((r) => `${r.host ?? ''}\0${r.session}`),
            )
            const add = refs.filter((r) => !already.has(`${r.host ?? ''}\0${r.session}`))
            if (add.length) n.receivers.mutate({ id: picking.id, add })
            setPicking(null)
          }}
          onClose={() => setPicking(null)}
        />
      )}
    </>
  )
}
